import { z } from 'zod';
import { JabberrAvatarId } from './appearance.js';

// Shared enumerations. Values match the text columns in packages/db exactly.
export const ReasoningEffort = z.enum(['low', 'high']);
export type ReasoningEffort = z.infer<typeof ReasoningEffort>;

export const Modality = z.enum(['text', 'voice']);
export type Modality = z.infer<typeof Modality>;

export const TurnStatus = z.enum(['pending', 'streaming', 'completed', 'cancelled', 'failed']);
export type TurnStatus = z.infer<typeof TurnStatus>;

export const TurnKind = z.enum(['user', 'assistant', 'system', 'summary']);
export type TurnKind = z.infer<typeof TurnKind>;

export const TurnPartKind = z.enum(['text', 'reasoning', 'tool_call', 'tool_result', 'attachment_ref']);
export type TurnPartKind = z.infer<typeof TurnPartKind>;

export const ActorKind = z.enum(['user', 'assistant', 'worker']);
export type ActorKind = z.infer<typeof ActorKind>;

/** 'meeting' is the hidden conversation behind a voice meeting (docs/adr/OPEN-31): replies live there,
 *  but no list, search or picker ever shows it. */
export const ConversationKind = z.enum(['chat', 'review', 'meeting']);
export type ConversationKind = z.infer<typeof ConversationKind>;

export const ConversationStatus = z.enum(['active', 'archived']);
export type ConversationStatus = z.infer<typeof ConversationStatus>;

export const ProjectStatus = z.enum(['active', 'archived']);
export type ProjectStatus = z.infer<typeof ProjectStatus>;

/**
 * 'personal': one owner, no other members ever; created for every user on first sign-in or seed.
 * 'private': a project only its owner can see until they share it (kind becomes 'team'; docs/adr/OPEN-18).
 */
export const ProjectKind = z.enum(['team', 'personal', 'private']);
export type ProjectKind = z.infer<typeof ProjectKind>;

/** The role prompt a conversation runs on (docs/adr/OPEN-15, OPEN-37). Definitions live in roles.ts. */
export const ConversationRole = z.enum(['product_owner', 'open', 'client_partner', 'marketing', 'strategy', 'sales_executive', 'client_sales_executive']);
export type ConversationRole = z.infer<typeof ConversationRole>;

export const ListenMode = z.enum(['always', 'mentions', 'keen', 'silent']);
export type ListenMode = z.infer<typeof ListenMode>;

/** Who may be shown a piece of content. Set from where it originated, never judged from its text. */
export const Audience = z.enum(['internal', 'client']);
export type Audience = z.infer<typeof Audience>;

/** Which version of reality a statement is about: what is released, what is in development, or neither. */
export const Frame = z.enum(['production', 'dev', 'general']);
export type Frame = z.infer<typeof Frame>;

/** Per-project state documents pushed in by CI or published by a member (docs/adr/OPEN-16). */
export const StateDocKind = z.enum(['production', 'dev', 'backlog', 'feedback']);
export type StateDocKind = z.infer<typeof StateDocKind>;

export const StateVersions = z.partialRecord(StateDocKind, z.number().int().positive());
export type StateVersions = z.infer<typeof StateVersions>;

export const MemoryScope = z.enum(['project', 'user']);
export type MemoryScope = z.infer<typeof MemoryScope>;

export const MemoryKind = z.enum(['constraint', 'decision', 'open_question', 'fact', 'preference']);
export type MemoryKind = z.infer<typeof MemoryKind>;

export const MemoryStatus = z.enum(['active', 'proposed', 'retired']);
export type MemoryStatus = z.infer<typeof MemoryStatus>;

export const MemorySource = z.enum(['conversation', 'manual', 'feedback']);
export type MemorySource = z.infer<typeof MemorySource>;

/**
 * Membership on a project. A guest is a full user (Better Auth) who joined via invite: the owner adds
 * them by email on the existing magic-link path (the Communication Services mailer), and their
 * conversations run on a client-audience role so they only ever see production state documents.
 */
export const MemberRole = z.enum(['owner', 'member', 'guest']);
export type MemberRole = z.infer<typeof MemberRole>;

export const RecordingSource = z.enum(['upload', 'browser', 'vtt', 'meeting']);
export type RecordingSource = z.infer<typeof RecordingSource>;

/** 'live': a meeting's recording is being written segment by segment; it becomes 'ready' when the meeting ends. */
export const RecordingStatus = z.enum(['live', 'uploaded', 'queued', 'transcribing', 'ready', 'failed']);
export type RecordingStatus = z.infer<typeof RecordingStatus>;

export const MeetingStatus = z.enum(['live', 'ended']);
export type MeetingStatus = z.infer<typeof MeetingStatus>;

/** One spoken exchange in a meeting's transcript (docs/adr/OPEN-31). The recorder's segment shape,
 *  with the speaker's user id attached for people; Jabberr's replies carry speaker 'Jabberr'. */
export const TranscriptFragment = z.object({
  streamId: z.string().min(1),
  utteranceId: z.string().min(1),
  index: z.number().int().nonnegative(),
  /** Inclusive fragment range after presentation coalescence. Raw rows omit this. */
  lastIndex: z.number().int().nonnegative().optional(),
});
export type TranscriptFragment = z.infer<typeof TranscriptFragment>;

export const MeetingSegment = z.object({
  start: z.number().nonnegative(), // seconds since the meeting started
  end: z.number().nonnegative(),
  speaker: z.string().min(1),
  text: z.string(),
  fragment: TranscriptFragment.optional(),
  /** Completed speech playback, for explicit feedback and idempotent acknowledgements. */
  responseId: z.string().uuid().optional(),
  pieceIndex: z.number().int().nonnegative().optional(),
  /** Present for people; absent for Jabberr. */
  userId: z.string().optional(),
});
export type MeetingSegment = z.infer<typeof MeetingSegment>;

export const TranscribeJobStatus = z.enum(['queued', 'claimed', 'done', 'failed']);
export type TranscribeJobStatus = z.infer<typeof TranscribeJobStatus>;

export const ModelRequestOutcome = z.enum(['completed', 'cancelled', 'error', 'unavailable']);
export type ModelRequestOutcome = z.infer<typeof ModelRequestOutcome>;

/**
 * What became of a keen decision on a user turn, so no judged moment drops out of the export or the labelling
 * deck. With a probability: 'quiet' (below the threshold), 'spoke' (a reply started), 'superseded' (a yes, but
 * someone spoke or answered before the reply could start), 'busy' (a yes, but a reply was already streaming).
 * 'superseded' can also come without one: a newer meeting utterance interrupted the decision while it ran.
 * Without one, the reason there is none: 'timeout' (the decider's own abort), 'aborted' (shutdown or a caller's
 * abort), 'unavailable' (engine or sidecar unreachable), 'error' (any other failure), 'unparsed' (an answer that
 * was neither yes nor no).
 */
export const KeenOutcome = z.enum(['quiet', 'spoke', 'superseded', 'busy', 'skipped_rule', 'timeout', 'aborted', 'unavailable', 'error', 'unparsed']);
export type KeenOutcome = z.infer<typeof KeenOutcome>;
/** The outcomes that carry no probability: the decider gave no verdict. */
export const KEEN_NO_VERDICT: ReadonlySet<KeenOutcome> = new Set(['skipped_rule', 'timeout', 'aborted', 'unavailable', 'error', 'unparsed']);

/**
 * Why a turn exists beyond the conversation (docs/adr/OPEN-19, OPEN-21). A document kind marks a fixed user turn
 * (prompts/<kind>-turn.md) and the reply it produced; 'saved' marks any reply a person kept. Null for ordinary turns.
 */
export const DocumentKind = z.enum(['requirements', 'brief', 'decision', 'readout']);
export type DocumentKind = z.infer<typeof DocumentKind>;
export const TurnPurpose = z.enum([...DocumentKind.options, 'saved']);
export type TurnPurpose = z.infer<typeof TurnPurpose>;

export const TurnRating = z.enum(['up', 'down']);
export type TurnRating = z.infer<typeof TurnRating>;

export const DOCUMENT_KINDS: Record<DocumentKind, { label: string; description: string }> = {
  requirements: { label: 'Requirements', description: 'Numbered, testable requirements with acceptance criteria, ready to build from.' },
  brief: { label: 'Brief', description: 'One page for someone who was not in the conversation: what, who, why now, what we know.' },
  decision: { label: 'Decision memo', description: 'The options, a recommendation, what it costs and what would change our mind.' },
  readout: { label: 'Stakeholder readout', description: 'What a person should take away from a conversation: decisions, commitments, what is on them.' },
};

/**
 * The parking lot (anchor-loop decisions 2026-10-09 #6): sticky-note ideas from conversations, each with a short
 * write-up and a comment thread. Columns are where the idea stands; Building is reached when its requirements are
 * approved (in Loop, later) or set by a team member.
 */
export const ParkingLotColumn = z.enum(['someday', 'next_up', 'building', 'not_doing']);
export type ParkingLotColumn = z.infer<typeof ParkingLotColumn>;
export const PARKING_LOT_COLUMNS: Record<ParkingLotColumn, string> = {
  someday: 'Someday',
  next_up: 'Next up',
  building: 'Building',
  not_doing: 'Not doing',
};

export const ParkingLotSize = z.enum(['small', 'medium', 'large', 'unknown']);
export type ParkingLotSize = z.infer<typeof ParkingLotSize>;

/**
 * Parking lot Part 2 (anchor-loop decisions 2026-10-09 #6, #10): an idea's requirements sent to Loop for John's or
 * Adam's approval. waiting: in Loop; approved: Yard made or extended the project; sent_back: declined with a note;
 * expired: nobody decided in time; failed: approved, but Yard could not carry it out.
 */
export const ParkingLotLoopStatus = z.enum(['waiting', 'approved', 'sent_back', 'expired', 'failed']);
export type ParkingLotLoopStatus = z.infer<typeof ParkingLotLoopStatus>;
export const ParkingLotLoopMode = z.enum(['new', 'extend']);
export type ParkingLotLoopMode = z.infer<typeof ParkingLotLoopMode>;

export const ParkingLotStatus = z.enum(['active', 'archived']);
export type ParkingLotStatus = z.infer<typeof ParkingLotStatus>;

export const PARKING_LOT_TITLE_MAX = 120;
export const PARKING_LOT_SUMMARY_MAX = 200;
export const PARKING_LOT_WRITEUP_MAX = 4000;
export const PARKING_LOT_COMMENT_MAX = 4000;

/** Where a project shows in the left nav (2026-10-09 decision #9), set by its owner; null means the default. */
export const NavGroup = z.enum(['clients', 'products', 'platforms', 'just_me']);
export type NavGroup = z.infer<typeof NavGroup>;

/** Why a model call was made: the visible reply, the rolling summary checkpoint, memory extraction, or the keen interject decision (OPEN-30). */
export const ModelRequestPurpose = z.enum(['reply', 'summary', 'memory', 'decision']);
export type ModelRequestPurpose = z.infer<typeof ModelRequestPurpose>;

/** How prompt tokens were counted: the engine's POST /tokenize, or the chars-per-token estimate. */
export const TokenCountSource = z.enum(['tokenize', 'estimate']);
export type TokenCountSource = z.infer<typeof TokenCountSource>;

/** Error string a gateway yields (done.error) and the api stores (cancel_reason) when the prompt cannot fit the window. */
export const CONTEXT_TOO_LONG = 'context_too_long';

// Token estimate used when the engine's /tokenize is absent or slow. Measured on Jabberr's text with the
// Qwen3 tokenizer on 2026-09-16: 4.5 to 4.7 characters per token (docs/reviews/2026-09-16-v1-review.md).
export const CHARS_PER_TOKEN = 4.6;

export function estimateTokens(text: string): number {
  if (text.length === 0) return 0;
  // Integer arithmetic (chars * 10 / 46) so exact multiples do not round up by one in floating point.
  return Math.ceil((text.length * 10) / 46);
}

export const NodeKind = z.enum(['spark', 'mac', 'other']);
export type NodeKind = z.infer<typeof NodeKind>;

export const InferenceState = z.enum(['available', 'unavailable', 'unknown']);
export type InferenceState = z.infer<typeof InferenceState>;

// Timestamps cross the API as ISO-8601 strings.
export const IsoDate = z.string().min(1);
export type IsoDate = z.infer<typeof IsoDate>;

// --- Model messages ---
/** One tool call as the model emitted it (OpenAI shape). `arguments` is the raw JSON text. */
export const ToolCall = z.object({
  id: z.string(),
  name: z.string(),
  arguments: z.string(),
});
export type ToolCall = z.infer<typeof ToolCall>;

export const ChatMessage = z.object({
  role: z.enum(['system', 'user', 'assistant', 'tool']),
  content: z.string(),
  /** Assistant messages only: the calls the model made in that message. */
  toolCalls: z.array(ToolCall).optional(),
  /** Tool messages only: which call this answers. */
  toolCallId: z.string().optional(),
});
export type ChatMessage = z.infer<typeof ChatMessage>;

/** A tool the model may call, in the OpenAI function shape. `parameters` is a JSON schema object. */
export const ToolDefinition = z.object({
  name: z.string().min(1),
  description: z.string(),
  parameters: z.record(z.string(), z.unknown()),
});
export type ToolDefinition = z.infer<typeof ToolDefinition>;

// --- Transcripts ---
export const Segment = z.object({
  start: z.number().nonnegative(), // seconds
  end: z.number().nonnegative(), // seconds
  speaker: z.string().min(1), // "S1", "S2", ... by first appearance
  text: z.string(),
  fragment: TranscriptFragment.optional(),
  confidence: z.number().min(0).max(1).optional(),
});
export type Segment = z.infer<typeof Segment>;

export const SpeakerMap = z.record(z.string(), z.string());
export type SpeakerMap = z.infer<typeof SpeakerMap>;

// --- Turn part content, discriminated by turn_parts.kind ---
// truncated: the engine stopped at max_tokens (finish_reason length); the text is what was delivered.
export const TextPartContent = z.object({ text: z.string(), truncated: z.boolean().optional() });
/** Content of a turn of kind 'summary': the rolling checkpoint standing in for every turn with seq <= throughSeq. */
export const SummaryPartContent = z.object({ text: z.string(), throughSeq: z.number().int().nonnegative() });
export type SummaryPartContent = z.infer<typeof SummaryPartContent>;
export const ReasoningPartContent = z.object({ text: z.string() });
export const ToolCallPartContent = z.object({
  callId: z.string(),
  name: z.string(),
  args: z.record(z.string(), z.unknown()),
});
export const ToolResultPartContent = z.object({
  callId: z.string(),
  result: z.unknown(),
  error: z.string().optional(),
});
export const AttachmentRefPartContent = z.object({
  blobKey: z.string(),
  mime: z.string(),
  name: z.string().optional(),
  sizeBytes: z.number().int().nonnegative().optional(),
});
export type TextPartContent = z.infer<typeof TextPartContent>;
export type ReasoningPartContent = z.infer<typeof ReasoningPartContent>;

// --- Entities as they cross the API (JSON shapes) ---
export const User = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().email(),
  emailVerified: z.boolean(),
  image: z.string().nullable().optional(),
  createdAt: IsoDate,
  updatedAt: IsoDate,
});
export type User = z.infer<typeof User>;

export const Project = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  status: ProjectStatus,
  kind: ProjectKind,
  /** The Anchor Workbench project Save to the Workbench writes to (OPEN-34), set from Yard by anchor-link; null until then. */
  workbenchProjectId: z.string().nullable().optional(),
  /** The Anchor Yard account (client or brand) the project is filed under (OPEN-36, D27: never changes); null until filed. */
  accountId: z.string().nullable().optional(),
  /** Where the project shows in the left nav, chosen by an owner; null or absent: the default for its kind. */
  navGroup: NavGroup.nullable().optional(),
  /** A new-project idea from the parking lot: private, and its owner may invite teammates and guests (OPEN-38). */
  isIdea: z.boolean().optional(),
  createdBy: z.string(),
  createdAt: IsoDate,
  updatedAt: IsoDate,
});
export type Project = z.infer<typeof Project>;

export const ProjectMember = z.object({
  projectId: z.uuid(),
  userId: z.string(),
  role: MemberRole,
  createdAt: IsoDate,
  // email is left out when the caller is a guest: a client sees who is in the project, not how to reach them.
  user: User.pick({ id: true, name: true }).extend({ email: z.string().email().optional() }).optional(),
});
export type ProjectMember = z.infer<typeof ProjectMember>;

export const ProjectContextVersion = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  audience: Audience.default('internal'),
  version: z.number().int().positive(),
  content: z.string(),
  note: z.string().nullable(),
  publishedBy: z.string(),
  publishedAt: IsoDate,
});
export type ProjectContextVersion = z.infer<typeof ProjectContextVersion>;

/** One version of one state document. A new version exists only when the content hash changed. */
export const StateDoc = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  kind: StateDocKind,
  version: z.number().int().positive(),
  content: z.string(),
  contentHash: z.string(),
  /** Who sent it: 'ci', 'nightly', 'manual' or any short label the sender chooses. */
  source: z.string(),
  /** Commit, build id or release the content describes, when the sender knows it. */
  sourceRef: z.string().nullable(),
  /** Set when a member published it by hand; null for the ingest route. */
  publishedBy: z.string().nullable(),
  createdAt: IsoDate,
});
export type StateDoc = z.infer<typeof StateDoc>;

/** One thing Jabberr remembers across conversations (docs/adr/OPEN-17). Content, never telemetry. */
export const MemoryItem = z.object({
  id: z.uuid(),
  scope: MemoryScope,
  /** Set for scope 'project'. */
  projectId: z.uuid().nullable(),
  /** Set for scope 'user': the only person who ever sees the item. */
  userId: z.string().nullable(),
  kind: MemoryKind,
  text: z.string(),
  audience: Audience,
  frame: Frame,
  status: MemoryStatus,
  source: MemorySource,
  sourceConversationId: z.uuid().nullable(),
  sourceProjectId: z.uuid().nullable().optional(),
  sourceTurnId: z.uuid().nullable().optional(),
  sourceQuote: z.string().nullable().optional(),
  replacesMemoryId: z.uuid().nullable().optional(),
  replacementConfirmed: z.boolean().optional(),
  replacesUpdatedAt: IsoDate.nullable().optional(),
  createdBy: z.string(),
  createdAt: IsoDate,
  updatedAt: IsoDate,
});
export type MemoryItem = z.infer<typeof MemoryItem>;

export const Conversation = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  title: z.string().nullable(),
  kind: ConversationKind,
  createdBy: z.string(),
  status: ConversationStatus,
  reasoningEffort: ReasoningEffort,
  contextVersion: z.number().int().positive(),
  /** The client-layer version pinned at creation, null when the project had no client layer then.
   *  For client-audience conversations this is the meaningful version; contextVersion is 0 for them. */
  clientContextVersion: z.number().int().positive().nullable(),
  /** The role prompt's version pinned at creation; the builder renders that version's exact bytes. */
  globalPromptVersion: z.number().int().nonnegative(),
  /** Which role prompt the conversation runs on; fixed for its life (docs/adr/OPEN-15). */
  role: ConversationRole,
  /** State document versions pinned at creation, by kind (docs/adr/OPEN-16). A kind with no document is absent. */
  stateVersions: StateVersions,
  /** Seq of the last turn memory extraction has covered (docs/adr/OPEN-17). */
  memoryThroughSeq: z.number().int().nonnegative(),
  /** Web search tools on (docs/adr/OPEN-20); fixed at creation. */
  webSearch: z.boolean(),
  /** Group threads (OPEN-23): 'mentions' answers only @jabber turns; 'always' answers every user turn;
   *  'keen' answers when the decision sidecar says Jabberr has something to add. */
  listenMode: z.enum(['always', 'mentions', 'keen', 'silent']),
  transcriptId: z.uuid().nullable(),
  /** Reviews: the recording title captured at creation. The prefix heading reads this, never the renameable title. */
  transcriptTitle: z.string().nullable(),
  lastTurnSeq: z.number().int().nonnegative(),
  createdAt: IsoDate,
  updatedAt: IsoDate,
});
export type Conversation = z.infer<typeof Conversation>;

/** How full the next prompt is: promptTokens over budget (window minus output reserve minus slack). */
export const ContextUsage = z.object({
  usedFraction: z.number().min(0),
  window: z.number().int().positive(),
  budget: z.number().int().positive(),
  promptTokens: z.number().int().nonnegative(),
  source: TokenCountSource,
});
export type ContextUsage = z.infer<typeof ContextUsage>;

export const ConversationParticipant = z.object({
  conversationId: z.uuid(),
  actorId: z.string(),
  actorKind: ActorKind,
  joinedAt: IsoDate,
});
export type ConversationParticipant = z.infer<typeof ConversationParticipant>;

export const TurnPart = z.object({
  id: z.uuid(),
  turnId: z.uuid(),
  idx: z.number().int().nonnegative(),
  kind: TurnPartKind,
  content: z.record(z.string(), z.unknown()),
});
export type TurnPart = z.infer<typeof TurnPart>;

export const Turn = z.object({
  id: z.uuid(),
  conversationId: z.uuid(),
  seq: z.number().int().positive(),
  actorId: z.string(),
  kind: TurnKind,
  modality: Modality,
  status: TurnStatus,
  clientTurnId: z.string().nullable(),
  answeredRole: ConversationRole.nullable().optional(),
  rolePromptVersion: z.number().int().positive().nullable().optional(),
  purpose: TurnPurpose.nullable(),
  /** The reader's thumbs feedback on a completed reply, or null (the vision's Observe step). */
  rating: TurnRating.nullable(),
  /** Actor ids the message @-mentions (project member user ids, or 'assistant' for @jabber). */
  mentions: z.array(z.string()),
  /** Keen mode: the decider's probability that Jabberr should speak (null when no decision ran). */
  interjectP: z.number().min(0).max(1).nullable(),
  /** Keen mode: what became of the decision (null when none ran; absent from older payloads). */
  keenOutcome: KeenOutcome.nullable().optional(),
  createdAt: IsoDate,
  completedAt: IsoDate.nullable(),
  cancelReason: z.string().nullable(),
});
export type Turn = z.infer<typeof Turn>;

/** An output: the completed reply to a document turn, or a reply someone saved, as the Outputs page lists it. */
export const Output = z.object({
  turnId: z.uuid(),
  purpose: TurnPurpose,
  conversationId: z.uuid(),
  conversationTitle: z.string().nullable(),
  createdBy: z.string(),
  createdAt: IsoDate,
  /** The first line of the document, for the list. */
  preview: z.string(),
  /** Set once a person saved it to the Anchor Workbench (OPEN-34). */
  workbench: z.object({ number: z.number().int().positive(), id: z.string(), url: z.string(), savedAt: IsoDate }).nullable().optional(),
});
export type Output = z.infer<typeof Output>;

export const TurnWithParts = Turn.extend({
  parts: z.array(TurnPart),
  /** Display name of the human who made a user turn; null for the assistant and system turns. */
  actorName: z.string().nullable().optional(),
});
export type TurnWithParts = z.infer<typeof TurnWithParts>;

export const Recording = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  createdBy: z.string(),
  title: z.string(),
  source: RecordingSource,
  status: RecordingStatus,
  blobKey: z.string().nullable(),
  mime: z.string().nullable(),
  durationS: z.number().nullable(),
  sizeBytes: z.number().nullable(),
  chunkCount: z.number().int().nonnegative(),
  retentionUntil: IsoDate.nullable(),
  error: z.string().nullable(),
  createdAt: IsoDate,
  updatedAt: IsoDate,
});
export type Recording = z.infer<typeof Recording>;

export const Transcript = z.object({
  id: z.uuid(),
  recordingId: z.uuid(),
  language: z.string(),
  segments: z.array(Segment),
  speakerMap: SpeakerMap,
  wordCount: z.number().int().nonnegative(),
  asrModel: z.string(),
  diarizationModel: z.string().nullable(),
  createdAt: IsoDate,
  updatedAt: IsoDate,
});
export type Transcript = z.infer<typeof Transcript>;

/** A voice meeting (docs/adr/OPEN-31): its own LiveKit room, transcript and hidden conversation. */
export const Meeting = z.object({
  /** The assistant artwork everyone in this meeting sees (the starter's or an owner's choice). */
  jabberrAvatarId: JabberrAvatarId.optional(),
  /** Additive copy of jabberrAvatarId for clients that know every catalog id (see serializeMeeting). */
  jabberrAssistantImageId: JabberrAvatarId.optional(),
  id: z.uuid(),
  projectId: z.uuid(),
  title: z.string(),
  status: MeetingStatus,
  listenMode: ListenMode,
  createdBy: z.string(),
  startedAt: IsoDate,
  endedAt: IsoDate.nullable(),
  recordingId: z.uuid().nullable(),
  conversationId: z.uuid().nullable(),
});
export type Meeting = z.infer<typeof Meeting>;

export const TranscribeJob = z.object({
  id: z.uuid(),
  recordingId: z.uuid(),
  status: TranscribeJobStatus,
  workerId: z.string().nullable(),
  claimedAt: IsoDate.nullable(),
  finishedAt: IsoDate.nullable(),
  attempts: z.number().int().nonnegative(),
  error: z.string().nullable(),
  createdAt: IsoDate,
});
export type TranscribeJob = z.infer<typeof TranscribeJob>;

export const ModelRequest = z.object({
  id: z.uuid(),
  requestId: z.string(),
  conversationId: z.uuid(),
  projectId: z.uuid(),
  userId: z.string(),
  turnId: z.uuid(),
  deploymentId: z.string(),
  provider: z.string(),
  model: z.string(),
  reasoningEffort: ReasoningEffort,
  thinkingEnabled: z.boolean(),
  prefixHash: z.string(),
  contextVersion: z.number().int(),
  prefixTokens: z.number().int().nullable(),
  cachedTokens: z.number().int().nullable(),
  inputTokens: z.number().int().nullable(),
  reasoningTokens: z.number().int().nullable(),
  outputTokens: z.number().int().nullable(),
  trailingTokens: z.number().int().nullable(),
  ttfDeltaMs: z.number().int().nullable(),
  ttfContentMs: z.number().int().nullable(),
  totalMs: z.number().int().nullable(),
  tokensPerS: z.number().nullable(),
  concurrencyAtStart: z.number().int().nonnegative(),
  outcome: ModelRequestOutcome,
  modality: Modality,
  error: z.string().nullable(),
  truncated: z.boolean(),
  purpose: ModelRequestPurpose,
  promptTokenSource: TokenCountSource.nullable(),
  // Keen instrumentation (docs/research/2026-09-26-keen-instrumentation.md); null on rows written before it.
  /** When the gateway stream was opened (created_at is the insert, at the end of the call). */
  startedAt: IsoDate.nullable().optional(),
  /** 0 for the first gateway round of a turn, 1 and up for the rounds after tool calls. */
  roundIndex: z.number().int().nonnegative().optional(),
  /** Round 0's request_id on rounds 1 and later. */
  parentRequestId: z.string().nullable().optional(),
  /** Round 0 of a reply: from the start of generation to the first gateway call (planning, /tokenize, summary). */
  planMs: z.number().int().nullable().optional(),
  /** Tool time that ran before this round (the calls the previous round asked for). */
  toolMs: z.number().int().nullable().optional(),
  /** Gateway streams of any purpose already open in this api process when this one opened. */
  engineInflightAtStart: z.number().int().nonnegative().nullable().optional(),
  /** The user turn a keen decision judged, on the decision row and on every round of the reply it caused. */
  triggerTurnId: z.uuid().nullable().optional(),
  createdAt: IsoDate,
});
export type ModelRequest = z.infer<typeof ModelRequest>;

// Event types are content-free by rule: payloads carry ids and counts, never text.
export const EventType = z.enum([
  'provider_session_lifecycle',
  'project_created',
  'member_added',
  'conversation_created',
  'conversation_role_changed',
  'turn_appended',
  // Keen instrumentation (docs/research/2026-09-26-keen-instrumentation.md): one row per keen decision
  // outcome, and one per tool call in a reply.
  'keen_decision',
  'tool_call',
  'context_published',
  'context_pin_proposed',
  'context_pinned',
  'upload_complete',
  'transcript_ready',
  'transcript_failed',
  'heartbeat_missed',
  'node_heartbeat',
  'admin_user_created',
  'turn_rated',
  'state_doc_published',
  'memory_extracted',
  'memory_added',
  'memory_updated',
  'turn_saved',
  'turn_unsaved',
  'project_shared',
  'members_added_everyone',
  'meeting_participation',
  'meeting_feedback',
  'workbench_saved',
  'project_linked',
  'review_sign_in',
]);
export type EventType = z.infer<typeof EventType>;

export const Event = z.object({
  id: z.number().int(),
  type: z.string(),
  actorId: z.string().nullable(),
  projectId: z.uuid().nullable(),
  conversationId: z.uuid().nullable(),
  payload: z.record(z.string(), z.unknown()),
  createdAt: IsoDate,
});
export type Event = z.infer<typeof Event>;

export const InferenceNode = z.object({
  id: z.string(), // MagicDNS name
  kind: NodeKind,
  services: z.record(z.string(), z.unknown()),
  imageDigest: z.string().nullable(),
  versions: z.record(z.string(), z.unknown()),
  lastHeartbeatAt: IsoDate.nullable(),
  status: z.string(),
});
export type InferenceNode = z.infer<typeof InferenceNode>;
